import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Pencil,
  Plus,
  Trash2,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AURA_API,
  DEFAULT_AURA_COMPANY_CODE,
  createAuraCompany,
  createAuraProject,
  deleteAuraCompany,
  deleteAuraProject,
  listAuraCompanies,
  listAuraProjects,
  statusLabel,
  updateAuraCompany,
  updateAuraProject,
} from '@/lib/auraApi';
import { usePermissions } from '@/contexts/PermissionsContext';

const PROJECT_STATUSES = ['draft', 'discovery', 'in_progress', 'complete'];
const BOOTSTRAP_COMPANY_CODE = DEFAULT_AURA_COMPANY_CODE;

function errDetail(err, fallback) {
  return String(err?.response?.data?.detail || err?.message || fallback);
}

export default function TransformationsCompaniesAdmin() {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('transformations', 'update');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('company') || '';

  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Company dialogs
  const [companyDialog, setCompanyDialog] = useState(null); // create | edit
  const [companyForm, setCompanyForm] = useState({
    code: '',
    name: '',
    description: '',
    status: 'active',
  });
  const [deleteCompanyOpen, setDeleteCompanyOpen] = useState(false);

  // Project dialogs
  const [projectDialog, setProjectDialog] = useState(null); // create | edit
  const [projectForm, setProjectForm] = useState({
    id: '',
    name: '',
    description: '',
    status: 'draft',
  });
  const [deleteProjectId, setDeleteProjectId] = useState(null);

  const selected = useMemo(
    () => companies.find((c) => c.id === selectedId) || null,
    [companies, selectedId],
  );

  const isBootstrap = (company) =>
    company
    && (company.code || '').toUpperCase() === BOOTSTRAP_COMPANY_CODE;

  const loadCompanies = useCallback(async () => {
    if (!AURA_API) {
      setError('Transformations API is not configured. Set REACT_APP_BACKEND_URL.');
      setLoadingCompanies(false);
      return;
    }
    setLoadingCompanies(true);
    setError('');
    try {
      const rows = await listAuraCompanies(200);
      setCompanies(rows);
    } catch (err) {
      setError(errDetail(err, 'Failed to load companies'));
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const loadProjects = useCallback(async (companyId) => {
    if (!companyId) {
      setProjects([]);
      return;
    }
    setLoadingProjects(true);
    try {
      const rows = await listAuraProjects(200, { companyId });
      setProjects(rows);
    } catch (err) {
      setError(errDetail(err, 'Failed to load projects'));
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  useEffect(() => {
    if (selectedId) loadProjects(selectedId);
    else setProjects([]);
  }, [selectedId, loadProjects]);

  const selectCompany = (id) => {
    setSearchParams(id ? { company: id } : {});
  };

  const openCreateCompany = () => {
    if (!canUpdate) return;
    setCompanyForm({ code: '', name: '', description: '', status: 'active' });
    setCompanyDialog('create');
  };

  const openEditCompany = (company) => {
    if (!canUpdate) return;
    setCompanyForm({
      code: company.code || '',
      name: company.name || '',
      description: company.description || '',
      status: company.status || 'active',
    });
    setCompanyDialog('edit');
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    if (!canUpdate) return;
    setBusy(true);
    setError('');
    try {
      if (companyDialog === 'create') {
        const created = await createAuraCompany({
          code: companyForm.code.trim(),
          name: companyForm.name.trim(),
          description: companyForm.description.trim(),
          status: companyForm.status,
        });
        setCompanyDialog(null);
        await loadCompanies();
        selectCompany(created.id);
      } else if (companyDialog === 'edit' && selected) {
        await updateAuraCompany(selected.id, {
          name: companyForm.name.trim(),
          description: companyForm.description.trim(),
          status: companyForm.status,
        });
        setCompanyDialog(null);
        await loadCompanies();
      }
    } catch (err) {
      setError(errDetail(err, 'Failed to save company'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteCompany = async () => {
    if (!canUpdate || !selected) return;
    setBusy(true);
    setError('');
    try {
      await deleteAuraCompany(selected.id);
      setDeleteCompanyOpen(false);
      selectCompany('');
      await loadCompanies();
      setProjects([]);
    } catch (err) {
      setError(errDetail(err, 'Failed to delete company'));
    } finally {
      setBusy(false);
    }
  };

  const openCreateProject = () => {
    if (!canUpdate) return;
    setProjectForm({ id: '', name: '', description: '', status: 'draft' });
    setProjectDialog('create');
  };

  const openEditProject = (project) => {
    if (!canUpdate) return;
    setProjectForm({
      id: project.id,
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'draft',
    });
    setProjectDialog('edit');
  };

  const saveProject = async (e) => {
    e.preventDefault();
    if (!canUpdate || !selected?.id) return;
    setBusy(true);
    setError('');
    try {
      if (projectDialog === 'create') {
        await createAuraProject({
          name: projectForm.name.trim(),
          description: projectForm.description.trim(),
          company_id: selected.id,
          status: projectForm.status,
        });
      } else if (projectDialog === 'edit' && projectForm.id) {
        await updateAuraProject(projectForm.id, {
          name: projectForm.name.trim(),
          description: projectForm.description.trim(),
          status: projectForm.status,
        });
      }
      setProjectDialog(null);
      await loadProjects(selected.id);
    } catch (err) {
      setError(errDetail(err, 'Failed to save project'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeleteProject = async () => {
    if (!canUpdate || !deleteProjectId) return;
    setBusy(true);
    setError('');
    try {
      await deleteAuraProject(deleteProjectId);
      setDeleteProjectId(null);
      if (selected?.id) await loadProjects(selected.id);
    } catch (err) {
      setError(errDetail(err, 'Failed to delete project'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="transformations-companies-admin">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Transformations
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground">
            Companies
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Admin view to manage companies and their transformation projects.
            Open Dashboard to run discovery on a project.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/transformations">Open dashboard</Link>
          </Button>
          {canUpdate ? (
            <Button type="button" onClick={openCreateCompany} data-testid="admin-new-company">
              <Plus className="mr-2 h-4 w-4" />
              New company
            </Button>
          ) : null}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,320px)_1fr]">
        {/* Company list */}
        <section className="space-y-3" data-testid="admin-company-list">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Companies
          </h2>
          {loadingCompanies ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </p>
          ) : companies.length === 0 ? (
            <p className="text-sm text-muted-foreground">No companies yet.</p>
          ) : (
            <ul className="space-y-2">
              {companies.map((company) => {
                const active = company.id === selectedId;
                return (
                  <li key={company.id}>
                    <button
                      type="button"
                      onClick={() => selectCompany(company.id)}
                      data-testid={`admin-company-${company.id}`}
                      className={`
                        w-full rounded-xl border px-4 py-3 text-left transition-colors
                        ${active
                          ? 'border-violet-500/50 bg-[var(--inset)]'
                          : 'border-[var(--line)] bg-[var(--surface)] hover:border-violet-500/30'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="font-medium text-foreground">{company.name}</span>
                        <Badge variant="outline" className="ml-auto rounded-full text-[10px]">
                          {company.code}
                        </Badge>
                      </div>
                      {company.description ? (
                        <p className="mt-1 line-clamp-1 pl-6 text-xs text-muted-foreground">
                          {company.description}
                        </p>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Selected company projects */}
        <section className="space-y-4" data-testid="admin-company-detail">
          {!selected ? (
            <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--surface)] p-10 text-center text-sm text-muted-foreground">
              Select a company to manage its projects, or create a new company.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold text-foreground">{selected.name}</h2>
                    <Badge variant="outline" className="rounded-full">{selected.code}</Badge>
                    <Badge variant="outline" className="rounded-full capitalize">
                      {selected.status || 'active'}
                    </Badge>
                    {isBootstrap(selected) && (
                      <Badge className="rounded-full bg-cp-grad text-white">Seed</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {selected.description || 'No description'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canUpdate ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openEditCompany(selected)}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isBootstrap(selected)}
                        title={
                          isBootstrap(selected)
                            ? 'Bootstrap company (RTS) cannot be deleted'
                            : 'Delete company and all projects'
                        }
                        onClick={() => setDeleteCompanyOpen(true)}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Delete
                      </Button>
                    </>
                  ) : null}
                  <Button asChild size="sm" variant="secondary">
                    <Link to={`/transformations?company=${encodeURIComponent(selected.id)}`}>
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      Dashboard
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Projects ({projects.length})
                </h3>
                {canUpdate ? (
                  <Button type="button" size="sm" onClick={openCreateProject} data-testid="admin-new-project">
                    <Plus className="mr-1 h-3.5 w-3.5" />
                    New project
                  </Button>
                ) : null}
              </div>

              {loadingProjects ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading projects…
                </p>
              ) : projects.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[var(--line)] p-8 text-center text-sm text-muted-foreground">
                  No projects for this company yet.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-[var(--line)]">
                  <table className="w-full min-w-[520px] text-left text-sm">
                    <thead className="border-b border-[var(--line)] bg-[var(--inset)] text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Updated</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((project) => (
                        <tr
                          key={project.id}
                          className="border-b border-[var(--line)] last:border-0"
                          data-testid={`admin-project-${project.id}`}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">{project.name}</div>
                            {project.description ? (
                              <div className="line-clamp-1 text-xs text-muted-foreground">
                                {project.description}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 capitalize text-muted-foreground">
                            {statusLabel(project.status)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-muted-foreground">
                            {project.updated_at
                              ? new Date(project.updated_at).toLocaleDateString()
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                asChild
                              >
                                <Link to={`/transformations/projects/${project.id}`}>
                                  Open
                                </Link>
                              </Button>
                              {canUpdate ? (
                                <>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditProject(project)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDeleteProjectId(project.id)}
                                  >
                                    Delete
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Company create/edit */}
      <Dialog
        open={companyDialog === 'create' || companyDialog === 'edit'}
        onOpenChange={(open) => !open && setCompanyDialog(null)}
      >
        <DialogContent>
          <form onSubmit={saveCompany}>
            <DialogHeader>
              <DialogTitle>
                {companyDialog === 'create' ? 'New company' : 'Edit company'}
              </DialogTitle>
              <DialogDescription>
                {companyDialog === 'create'
                  ? 'Short unique code plus a display name. Projects hang under the company.'
                  : 'Update display fields. Company code cannot be changed.'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-code">Code</Label>
                <Input
                  id="admin-code"
                  value={companyForm.code}
                  disabled={companyDialog === 'edit'}
                  onChange={(e) =>
                    setCompanyForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                  }
                  required={companyDialog === 'create'}
                  maxLength={64}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-name">Name</Label>
                <Input
                  id="admin-name"
                  value={companyForm.name}
                  onChange={(e) => setCompanyForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-desc">Description</Label>
                <Textarea
                  id="admin-desc"
                  value={companyForm.description}
                  onChange={(e) =>
                    setCompanyForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={companyForm.status}
                  onValueChange={(v) => setCompanyForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setCompanyDialog(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !companyForm.name.trim()}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete company */}
      <Dialog open={deleteCompanyOpen} onOpenChange={setDeleteCompanyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete company?</DialogTitle>
            <DialogDescription>
              This permanently removes{' '}
              <strong>{selected?.name}</strong> and all of its projects, runs, and
              artifacts. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteCompanyOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDeleteCompany} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project create/edit */}
      <Dialog
        open={projectDialog === 'create' || projectDialog === 'edit'}
        onOpenChange={(open) => !open && setProjectDialog(null)}
      >
        <DialogContent>
          <form onSubmit={saveProject}>
            <DialogHeader>
              <DialogTitle>
                {projectDialog === 'create' ? 'New project' : 'Edit project'}
              </DialogTitle>
              <DialogDescription>
                Project under{' '}
                <strong>{selected?.name || selected?.code || 'company'}</strong>.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="proj-name">Name</Label>
                <Input
                  id="proj-name"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="proj-desc">Description</Label>
                <Textarea
                  id="proj-desc"
                  value={projectForm.description}
                  onChange={(e) =>
                    setProjectForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={projectForm.status}
                  onValueChange={(v) => setProjectForm((f) => ({ ...f, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {statusLabel(s)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setProjectDialog(null)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || !projectForm.name.trim()}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete project */}
      <Dialog open={Boolean(deleteProjectId)} onOpenChange={(open) => !open && setDeleteProjectId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete project?</DialogTitle>
            <DialogDescription>
              This permanently deletes the project and related runs/artifacts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteProjectId(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDeleteProject} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
